import React, { useState, useEffect, useContext } from "react";
import { BrowserRouter, StaticRouter, Route, Switch } from "react-router-dom";
import Header from "components/header/header";
import Footer from "components/footer/footer";
import Home from "components/home/Home";
import DynamicPage from "components/content/DynamicPage";
import ImpactHub from "components/content/ImpactHub/ImpactHub";
import PartnersPage from "components/content/Partners/Partners"
import DollarMatching from "components/content/Partners/DollarMatching"
import * as Common from "components/common";
import { locales, isJson, getEnvironment, getAssetUrl, getImage, getConfigUrl, getUrlLocale } from "components/utils";
import { CMSContext, ConfigContext } from "./context/AppContext";
import MoConfiguration from 'MoConfiguration';
import defaultConfig from 'resources/json/defaultConfig.json';
import { CmsLogin, CmsLogout, CmsPreview } from "components/cms";
import { Helmet } from "react-helmet";
import { LiveChatWidget } from '@livechat/widget-react'
import { OptimizelyProvider, createInstance}from '@optimizely/react-sdk';
import { PreviousUrlProvider } from './context/PreviousUrlContext';
import LiveChatButton from "components/common/Button/LiveChatButton";
import { initMixpanel } from "components/tracking/mixpanel";

const MoRouter = () => {
  let previewCms = null;
  const [cms, setCms] = useState(false);
  const [hasJSON, setHasJSON] = useState(false);
  const [liveChatEnabled, setLiveChatEnabled] = useState(false);
  const [liveChatActivated, setLiveChatActivated] = useState(false);
  const [liveChatReady, setLiveChatReady] = useState(false);
  const [config, setConfig] = useState(useContext(ConfigContext));
  const [randomKey, setRandomKey] = useState(Math.random());
  const [optimizely, setOptimizely] = useState();
  const [optimizelyEnabled, setOptimizelyEnabled] = useState(false);
  let routes = [];
  

  useEffect(() => {
    // Listen out for any callbacks to enable LiveChat
    window.addEventListener("enableLiveChat", (e) => {
      setLiveChatEnabled(true);
    });

    const enableOptimizelyHandler = () => {
      setOptimizelyEnabled(true);
    }
      
    // Listen out for any callbacks to enable Optimizely
    window.addEventListener("enableOptimizely", enableOptimizelyHandler);

    (async () => {
      setAppLanguage();
      
      // Fetch data
      fetchToConfigJson();
  
      // Fetch cms json file
      await fetchToCmsJson();
      // Event listeners
      window.addEventListener("changeCms", (e) => {
        if (e.detail && e.detail.cms) {
          setCms(e.detail.cms);
        }
      });
  
       // for Cms Draft Preview
       window.addEventListener('message', (event) => {
        if (event.data && event.data !== 'undefined' && typeof(event.data) === 'string' && isJson(event.data)) {
            let eventData = JSON.parse(event.data);
            /* Don't render preview if
              - receiving empty message.
              - message doesn't have story data structure (story data object has only 1 key which is story id)
            */
            if((eventData && eventData.value === false) || Object.keys(eventData).length > 1 || !isStoryData(eventData)) return;
            renderPreview(eventData);
        }
      });
    })()

    // cleanup event listener
    return () => {
      window.removeEventListener("enableOptimizely", enableOptimizelyHandler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

function isStoryData(eventData) {
  for (let id in eventData){
      if(!eventData[id] || typeof eventData[id] !== 'object' ||!eventData[id]?.hasOwn('title') || !eventData[id]?.hasOwn('url')){
          return false;
      }
  }

  return true;
}

  useEffect(() => {

    //Initialise mixpanel
    if(config.getValue('app.tracking.mixpanel.token')){
      initMixpanel(config);
    }
    // If it's not prerender
    if(window.navigator.userAgent !== 'mo-site-pre-render'){

      if(config.getValue('app.tracking.livechat.enabled')){
        // set up LiveChat
        setupLiveChat();
      }
      if(config.getValue('app.tracking.cookies.onetrust.enabled')){
        // Install our OneTrust cookie solution, if enabled
        setUpOnetrustCookie();
        // Set up GTM
        // setUpGTMAfterOneTrust();
        setUpGTM();
      }

      if(config.getValue('app.tracking.optimizely.enabled')){
        // set up Optimizely
        setUpOptimizely();
      }

      if(config.getValue('app.tracking.attribution.enabled')){
        // set up Attribution tracking
        setUpAttribution();
      }
  
    }
   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // LiveChat
  useEffect(() => {
    // Event listener to disable/enable LiveChat in uat/production
    // Default
    // if (getEnvironment() === "uat") {
    //   let hideChatInterval = setInterval(() => {
    //     if (document.getElementById("chat-widget-container")) {
    //       console.log("Hide LiveChat interval");
    //       hideLiveChat();
    //       clearInterval(hideChatInterval);
    //     }
    //   }, 500);
    // }
    window.addEventListener("disableLiveChat", (e) => {
      if (e.detail && e.detail.env) {
        console.log("Disable Live Chat");
        switch (e.detail.env) {
          default:
          case "uat":
            if (getEnvironment() === "uat") {
              hideLiveChat(e.detail.disable);
            }
            break;
          case "production":
            if (getEnvironment() === "prod") {
              hideLiveChat(e.detail.disable);
            }
            break;
          case "local":
            if (getEnvironment() === "local") {
              hideLiveChat(e.detail.disable);
            }
            break;
        }
      }
    });
  }, []);

  //Enable Optimizely when optimizelyEnabled is set
  useEffect(() => {
    if(optimizelyEnabled){
      setOptimizelyScripts();
    }
  }, [config, optimizelyEnabled]);

  /* 
  MWD-6201: hide Livechat by JS console
  Example: 
    - To disable: window.dispatchEvent(new Event('disableLiveChat'));
    - To disable/enable: window.dispatchEvent(new CustomEvent('disableLiveChat', {detail: {env:'uat', disable: true}}));
  */
  function hideLiveChat(hide) {
    let liveChatContainer = document.getElementById("chat-widget-container");
    if (liveChatContainer) {
      liveChatContainer.style.display =
        hide === undefined || hide ? "none" : "block";
      if (liveChatContainer.style.display === "block") {
        liveChatContainer.style.width = "281px";
        liveChatContainer.style.height = "63px";
      }
    }
  }

  /**
   * Logic to determine from our configs if LiveChat is enabled for the current country
   * (Note: if using OneTrust to handle cookie compliance, we will still need to check OneTrust cookies on top of this setting)
   * @returns {boolean}
   */
  function liveChatConfigEnabled(){
    return config.getValue('app.tracking.livechat.enabled') === '1';
  }

  function optimizelyConfigEnabled(){
    return config.getValue('app.tracking.optimizely.enabled') === '1';
  }

  /**
   * Logic to determine from our configs if Attribution tracking is enabled for the current country
   * @returns {boolean}
   */
  function attributionConfigEnabled(){
    return config.getValue('app.tracking.attribution.enabled') === '1';
  }

  function setUpOptimizely() {
    // Only set up Optimizely integration if our configs have it enabled in the first place
    if (optimizelyConfigEnabled()) {
      // If we're not using OneTrust, we will purely use our config settings to decide whether to enable Optimizely or not
      if (!oneTrustConfigEnabled()) {
        setOptimizelyEnabled(true);
      } else {
        // When OneTrust is enabled, don't just initialise Optimizely. We need to let OneTrust tell our page that
        // the "Functional" cookie category has been enabled by the user, via the below code and run the callback.
        // The callback itself will enable Optimizely in React
        const optimizelyScript = document.createElement('script');
        optimizelyScript.type = 'text/plain';
        optimizelyScript.setAttribute('class', 'optanon-category-C0003');
        optimizelyScript.async = false;
        optimizelyScript.innerHTML = "var enableOptimizely = function(){window.dispatchEvent(new CustomEvent('enableOptimizely'))};" +
          "OneTrust.InsertHtml('<div></div>', 'body', enableOptimizely, null, '3');";
        const head = document.querySelector('head');
        head.insertBefore(optimizelyScript, head.firstChild);
      }
    }
  }

  /**
   * Helper function to enable Optimizely scripts and initialize the SDK
   */
  function setOptimizelyScripts() {
    const optimizelyScript = document.createElement('script');
    optimizelyScript.setAttribute('src',`https://cdn.optimizely.com/js/${ config.getValue('app.tracking.optimizely.code')}.js`);
    const head = document.querySelector('head');
    const metaCollection = document.getElementsByTagName("META");
    const optimizely = createInstance({
      sdkKey: config.getValue('app.tracking.optimizely.code'),
      //datafile: window.optimizelyDatafile,
      eventBatchSize: 10, // Cannot change in JavaScript (Browser) environment.
      eventFlushInterval: 2000, // Cannot change in JavaScript (Browser) environment.
    });
    setOptimizely(optimizely);
    head.insertBefore(optimizelyScript, metaCollection[metaCollection.length-1].nextSibling);
  }

  /**
   * Set up Attribution tracking by loading the PHP-managed attribution script
   * This script captures URL parameters and referrer data for internal analytics
   * Includes error isolation to prevent any attribution script issues from affecting the PWA
   */
  function setUpAttribution() {
    if (attributionConfigEnabled()) {
      try {
        const currentDomain = window.location.origin;
        
        // Create attribution script element
        const attributionScript = document.createElement('script');
        attributionScript.src = `${currentDomain}/assets/scripts/helper/attributionHelper.js`;
        attributionScript.async = true;
        
        // Handle script loading errors
        attributionScript.onerror = () => {
          console.error('Failed to load attribution helper script');
        };

        // Handle runtime errors within the attribution script
        // by wrapping it in an isolated execution context
        attributionScript.onload = () => {
          try {
            // Script loaded successfully, but we'll catch any runtime errors
            // The attribution script should handle its own errors, but this provides extra safety
            if (typeof window.mov !== 'undefined' && window.mov.attribution) {
              console.log('Attribution tracking initialized successfully');
            }
          } catch (error) {
            console.error('Runtime error in attribution script:', error);
          }
        };

        // Append script to head
        const head = document.querySelector('head');
        head.appendChild(attributionScript);

      } catch (error) {
        console.error('Error setting up attribution tracking:', error);
      }
    }
  }

  function setupLiveChat() {
    // Only set up LiveChat integration if our configs have it enabled in the first place
    if (liveChatConfigEnabled()) {
      // If we're not using OneTrust, we will purely use our config settings to decide whether to show LiveChat or not
      if (!oneTrustConfigEnabled()) {
        setLiveChatEnabled(true);
      } else {
        // When OneTrust is enabled, don't just initialise LiveChat. We need to let OneTrust tell our page that
        // the "Functional" cookie category has been enabled by the user, via the below code and run the callback.
        // The calback itself will enable livechat in react
        const liveChatScript = document.createElement('script');
        liveChatScript.type = 'text/plain';
        liveChatScript.setAttribute('class', 'optanon-category-C0003');
        liveChatScript.async = false;
        liveChatScript.innerHTML = "var enableLiveChat = function(){window.dispatchEvent(new CustomEvent('enableLiveChat'))};" +
          "OneTrust.InsertHtml('<div></div>', 'body', enableLiveChat, null, '3');";
        const head = document.querySelector('head');
        head.insertBefore(liveChatScript, head.firstChild);
      }
    }
  }

  /**
   * Logic to determine from our configs if OneTrust has been enabled for the current country
   * @returns {boolean}
   */
  function oneTrustConfigEnabled(){
    return config.getValue('app.tracking.cookies.onetrust.enabled') === '1' && config.getValue('app.tracking.cookies.onetrust.scriptid');
  }

  /**
   * Logic to set up GTM after OneTrust cookie. if OneTrust could be load successfully
   */
  // function setUpGTMAfterOneTrust (){
  //   // Maximum attemps and delay time for each attemp.
  //   const maxAttemps = 50, delay = 100;
  //   let count = 0;
  //   const checkingOneTrust = () => {
  //     if(count === maxAttemps) {
  //       console.warn(`Failed to load OneTrust after ${count} retries. Now set up GTM`);
  //       setUpGTM();
  //     }
  //     // Waiting for OneTrust to load...
  //     if (typeof window.OneTrust === 'undefined') {
  //       console.log(`Waiting ${delay} for OneTrust script to load...`);
  //       count++;
  //       // Wait before trying again
  //       setTimeout(checkingOneTrust, delay);
  //     } else {
  //       console.log('OneTrust loaded successfully. Now set up GTM');
  //       setUpGTM();
  //     }
  //   }
  //   // Execute
  //   checkingOneTrust();
  // }

  /**
   * Install the Google Tag Manager (GTM)
   *
   * If we have OneTrust Cookie Management enabled, we will delay the gtm.js script from init'ing until OneTrust
   * fires up and enables it.
   * Note: We are setting gtm.js to belong to the OneTrust "Strictly Necessary" category, which will always be on, but
   * this way it still won't enable the below GTM code until it's processed the enclosing <script type=text/plain> tag
   * and converted it to text/javascript
   */
  function setUpGTM(){
    let gtmExtraParams = '';
    let gtmContainer = "GTM-T4M6JQ";

    const gtmScript = document.createElement('script');
    gtmScript.async = true;

    if (oneTrustConfigEnabled()) {
      gtmScript.type = 'text/plain';
      gtmScript.setAttribute('class', 'optanon-category-C0001');
    } else {
      gtmScript.type = 'text/javascript';
    }

    // UAT/dev should have extra debugging/previewing params appended
    if (getEnvironment() !== "prod") {
      gtmExtraParams = "+'&gtm_auth=eZPOPF6nHyHIltTgoy974w&gtm_preview=env-31&gtm_cookies_win=x'";
    }

    gtmScript.innerHTML = "window.dataLayer = window.dataLayer || [];\n" +
      "        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\n" +
      "            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\n" +
      "          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n" +
      "          'https://www.googletagmanager.com/gtm.js?id='+i+dl"+gtmExtraParams+";f.parentNode.insertBefore(j,f);\n" +
      "        })(window,document,'script','dataLayer','"+gtmContainer+"');";

    const head = document.querySelector('head');

    if (oneTrustConfigEnabled()) {
      // OneTrust Enabled, insert GTM *after* the main OneTrust installation
      const otStub = document.querySelector('#oneTrustInitStub');
      head.insertBefore(gtmScript, otStub.nextSibling);

    } else {
      // No OneTrust, insert GTM as high as possible
      head.insertBefore(gtmScript, head.firstChild);
    }

    //set up GTM for youtube iframe - requires Functional Cookies consent (C0003)
    const ytIframeScript = document.createElement('script');
    ytIframeScript.async = true;
    ytIframeScript.src = 'https://www.youtube.com/iframe_api';

    if (oneTrustConfigEnabled()) {
      ytIframeScript.type = 'text/plain';
      ytIframeScript.setAttribute('class', 'optanon-category-C0003');
    } else {
      ytIframeScript.type = 'text/javascript';
    }

    head.insertBefore(ytIframeScript, head.firstChild);
  }
  /**
   *
   * @returns {boolean} Return true if OneTrust enabled and integration code was included
   */
  function setUpOnetrustCookie(){
    if(oneTrustConfigEnabled()){
      /**
       * OneTrust expects the following JS to be rendered at the top of the <head> section.
       * We add the two script tags one at a time below
       *
       *  <!-- OneTrust Cookies Consent Notice start for movember.com -->
          <script src="https://cdn-au.onetrust.com/scripttemplates/otSDKStub.js" data-document-language="true" type="text/javascript" charset="UTF-8" data-domain-script="ea5960ce-518b-4f0c-be23-59cff9958b91" ></script>
          <script type="text/javascript">
            function OptanonWrapper() { }
          </script>
          <!-- OneTrust Cookies Consent Notice end for movember.com -->
       *
       *
       * @type {HTMLScriptElement}
       */

      // The OptanonWrapper bit
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = false;
      s.innerHTML = "function OptanonWrapper() { }";
      let head = document.querySelector('head');
      head.insertBefore(s, head.firstChild);
      // Now the main OneTrust JS include
      const otScript = document.createElement('script');
      otScript.setAttribute('id', 'oneTrustInitStub');
      otScript.setAttribute('type', 'text/javascript');
      otScript.setAttribute('charset', 'UTF-8');
      otScript.setAttribute('data-document-language', 'true');
      otScript.setAttribute('src', 'https://cdn-au.onetrust.com/scripttemplates/otSDKStub.js');
      otScript.setAttribute('data-domain-script', `${config.getValue('app.tracking.cookies.onetrust.scriptid')}${config.getValue('app.tracking.cookies.onetrust.testing') === '1' ? '-test' : ''}`);
      head = document.querySelector('head');
      head.insertBefore(otScript, head.firstChild);

      return true;
    }
    return false;
  }

  /**
 * Builds and returns standard headers for fetch requests.
 * - Adds no-cache headers to prevent stale content.
 * - Adds pre-rendering specific headers if running on cloudfront or localhost.
 * 
 * @returns {Headers} Configured headers object
 */
const getRequestHeaders = () => {
  const { country, language } = getUrlLocale();

  // Check if the current language is the default for the country
  const isDefault = locales?.[country]?.language?.some(
    (l) => l.code === language && l.default
  );

  // Construct locale code for header (e.g., "ca/fr" or just "ca" if default)
  const localeCode = isDefault ? country : `${country}/${language}`;

  let headers = new Headers();
  // Add no-cache to headers to make sure it will get new changes
  headers.append('pragma', 'no-cache');
  headers.append('cache-control', 'no-cache');


  // Add extra headers when on a pre-rendering environment
  if (window.location.hostname.includes("cloudfront.net")) {
    headers.append('x-mov-locale', localeCode);     
    headers.append('accept', 'application/json');   
  }

  return headers;
};


  function fetchToConfigJson() {
    const {country, language} = getUrlLocale();
    let url = getConfigUrl(country);
    const headers = getRequestHeaders();

    fetch(url, { headers: headers })
      .then((res) => {
        return res.json();
      })
      .then(
        (result) => {
          let configuration = new MoConfiguration();
          let configs = {...configuration.config, ...result};
          // set current URL language to config
          configs.language = language;
          configs.country = country;
          setConfig(new MoConfiguration(configs));
        },
        (error) => {
          console.log("Request error " + error);
          //if(getEnvironment() === 'local'){
            let configuration = new MoConfiguration();
            let configs = {...configuration.config, ...defaultConfig};
            configs.language = language;
            configs.country = country;
            setConfig(new MoConfiguration(configs));
          //}
        }
      );
  }

  const fetchToCmsJson = async () => {
    const { country, language } = getUrlLocale();
    const isDefault = locales[country]?.language?.find(locale => locale.code === language)?.default;
    const headers = getRequestHeaders();
  
    const primaryUrl = getAssetUrl("cms", country, isDefault ? "" : language);
    const fallbackUrl = getAssetUrl("cms", country);
  
    const fetchJson = async (url) => {
      try {
        const res = await fetch(url, { headers });
        // We check the response's Content-Type to ensure it's JSON before attempting to parse.
        // This prevents errors if the response is HTML (like a 404 page) or any non-JSON format.
        const contentType = res.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          return await res.json();
        }
      } catch (err) {
        console.warn(`Fetch failed for ${url}:`, err);
      }
      return null;
    };
  
    try {
      let sitemapJson = await fetchJson(primaryUrl);
      if (sitemapJson) {
        console.log(`Loaded sitemap JSON from: ${primaryUrl}`);
      } else {
        // Fallback to country-only URL if the first failed
        sitemapJson = await fetchJson(fallbackUrl);
        if (sitemapJson) {
          console.log(`Loaded sitemap JSON from fallback URL: ${fallbackUrl}`);
        }
      }

      if (sitemapJson) {
        combineCms(sitemapJson);
      } else {
        console.warn("Failed to load CMS JSON from both primary and fallback URLs.");
      }
    } catch (error) {
      console.error("Unexpected error in fetchToCmsJson:", error);
    }
  };
  
  
  function combineCms(cmsJson) {
    if (cmsJson) {
      setCms(cmsJson);
      previewCms = cmsJson;
      // Flag JSON has loaded
      setHasJSON(true);
    }
  }

  function setAppLanguage() {
    const { country, language } = getUrlLocale();
    // Country code
    if ( country && country !== config.getCountry() ) {
      config.setCountry(country);
    } else {
        const { hostname } = window.location;
        if (hostname.indexOf('cloudfront.net') < 0) {
            const locale = new URLSearchParams(window.location.search).get('locale');
            if (locale) {
              const [countryPre, languagePre] = locale.split("/");
              if (countryPre.length === 2) {
                config.setCountry(countryPre);
                if(languagePre && languagePre.length === 2) {
                  const supportedLanguages = locales?.[countryPre]?.language;
                  if (supportedLanguages) {
                    const languageMatch = supportedLanguages.find((lang) => lang.code === languagePre);
                    if (languageMatch) {
                      config.setLanguage(languagePre);
                    }
                  }
                }
              }
            }  
        } else {
          const localeCode = new URLSearchParams(window.location.search).get('localeCode');
          const countryCode = localeCode?.split('-')[1];
          if (countryCode) {
            config.setCountry(countryCode);
          }
        }
    }

    // Language code
    const countryLanguages = locales?.[country]?.language ? locales[country].language : [];
    if (language && language !== config.getLanguage()) {
      const languageMatch = countryLanguages.find((lang) => lang.code === language);
      if (languageMatch) {
        config.setLanguage(language);
      }
    }

    // TODO: in diffrent ticket to cover all T2 locales
    // Direct user browser language to current supported language of current locale
    // else if (countryLanguages.length > 1 && navigator?.language && !getCookie('language-bar-is-disabled')) {
    //   const supportedLanguage = navigator.language.substring(0, 2);
    //   const supportedLanguageMatch = countryLanguages.find((lang) => !lang.default && lang.code === supportedLanguage);
    //   if (supportedLanguageMatch) {
    //     config.setLanguage(supportedLanguage);
    //     window.location = `/${supportedLanguage}/`;
    //   }
    // }
    
    setConfig(config);
  }



  function renderPreview(json) {
    let newCms = previewCms;
    for (let id in json) {
      if ( newCms && newCms.components && typeof newCms.components[id] === "undefined") {
        newCms["pages"]["stories"]["components"].push({
          id: id,
          title: json[id]["title"],
          url: json[id]["url"],
          components: [id],
        });
      }
      newCms.components[id] = json[id];
      setCms(newCms);
      // Need to re-render current page to see new changes.
      // Can NOT reload the page as it would retrieve the cms again
      // Generate new random key in Article route for re-rendering article page in react-router
      setRandomKey(Math.random());
    }
  }

  return <React.Fragment>{renderRouter()}</React.Fragment>;

  function renderRouter(params) {
    let location = params && params.location ? params.location : "",
      RouterType = params && params.ssr ? StaticRouter : BrowserRouter;

    const returnPage = () => {

      return (
        <>
          {
          
          <ConfigContext.Provider value={config}>
          <CMSContext.Provider value={cms}>
            {/* Metadata */}
            {cms && renderMetaTags()}

            {optimizelyEnabled && <OptimizelyProvider
            optimizely={optimizely}
            // user={{id: '<Your_User_Id>'}}
            ></OptimizelyProvider>}
            <div id="clipboard-container"></div>
            {/* React Router */}
            <RouterType location={location}>
              <Header cms={cms} />
              <CmsPreview />
              <div id="content-container">
                <PreviousUrlProvider>
                  <Switch>{getRoutes()}</Switch>
                </PreviousUrlProvider>
              </div>
            </RouterType>
            {/* Live chat */}
            {liveChatEnabled &&
              <>
                {!(liveChatActivated && liveChatReady) && <LiveChatButton text={cms.snippets['text-livechat-button']?.text || 'Contact Us'} loading={liveChatActivated && !liveChatReady} onClick={() => setLiveChatActivated(true)} />}
                {liveChatActivated && <LiveChatWidget
                  license={config.getValue('app.tracking.livechat.code')}
                  group={config.getValue('app.tracking.livechat.lc.group')}
                  visibility="maximized" //maximized - minimized
                  onReady={() => {
                    console.log('LiveChat widget is ready');
                    setLiveChatReady(true);
                  }}
                />}
              </>
            }
            <Footer cms={cms} />
            <div id="json-loaded"></div>
          </CMSContext.Provider>
          </ConfigContext.Provider>
          
          }
        </>
      );
    };

    const returnPreRenderedHTML = () => {
      let innerHTML = window.__PRERENDER_HTML__;
      return (
        <div id="noJSON" dangerouslySetInnerHTML={{ __html: innerHTML }}></div>
      );
    };

    return (
      <React.Fragment>
        {!hasJSON ? returnPreRenderedHTML() : returnPage()}
        </React.Fragment>
    );
  }

  function renderMetaTags() {
    const { country, language } = getUrlLocale();
    const firstLanguage = locales?.[country]?.language[0];
    let languageCode = language || (firstLanguage?.default ? firstLanguage.code : config.getLanguage());
    let countryCode = country || config.getCountry();

    return (
      <React.Fragment>
         {/* React Helmet to set attributes for html tag and onetrust cookie script */}
         <Helmet
          htmlAttributes={{
            xmlns: "http://www.w3.org/1999/xhtml",
            "xmlns:fb": "http://www.facebook.com/2008/fbml",
            "xmlns:og": "http://ogp.me/ns#",
            "lang": `${languageCode}-${(countryCode === 'uk' ? 'gb' : countryCode).toUpperCase()}`,
            "xml:lang": `${languageCode}-${(countryCode === 'uk' ? 'gb' : countryCode).toUpperCase()}`,
          }}
        />
        {/* React Helmet to manage meta tags in document head */}
        { cms.siteSettings && cms.siteSettings.metadata && (
          <Helmet>
            <meta
              name="application-name"
              content={cms.siteSettings.metadata.title}
            />
            <meta
              name="description"
              content={cms.siteSettings.metadata.description}
            />
            <link
              rel="icon"
              href={getImage(
                cms.siteSettings.metadata["fav-icon"],
                "small"
              )}
            />
            <link
              rel="apple-touch-icon"
              href={getImage(
                cms.siteSettings.metadata["fav-icon"],
                "small"
              )}
            />
            {/* Open Graph meta tags */}
            <meta property="og:url" content={window.location.href} />
            <meta
              property="og:locale"
              content={`${config.getLanguage()}_${config.getCountry() === 'uk' ? 'gb' : config.getCountry()}`}
            />
            <meta
              property="og:title"
              content={cms.siteSettings.metadata.title}
            />
            <meta property="og:type" content="website" />
            <meta
              property="og:image"
              content="https://cdn.movember.com/uploads/images/Logos/Movember_Iconic%20Mo_Black.jpg"
            />
            <meta
              property="og:description"
              content={cms.siteSettings.metadata.description}
            />
          </Helmet>
        )}
      </React.Fragment>
    );
  }

  function getRoutes() {
    // Render Routes from static pages and dynamic pages from cms context
    // Static pages
    let staticPages = {   
      impact: { url: '/impact' }, 
      partners: { url: '/friends'},
      dollarMatching: { url: '/friends/dollar-matching'},
      'cms-login': { url: '/cms-login'}
    };

    // Small fix for allowing different routes for static pages
    // If the language is part of the path we need to 
    // update the static route to reflect this.
    // TODO soon we will move static pages to pages
    const { pathname } = window.location;
    if (pathname.includes(`/${config.getLanguage()}/`)) {
      Object.keys(staticPages).forEach((staticPage) => {
        staticPages[staticPage] = {"url": `/${config.getLanguage()}${staticPages[staticPage]["url"]}`};
      });
    }

    cms.pages = {...cms.pages, ...staticPages};

    for (const pageName in cms.pages) {
      let route = cms.pages[pageName];
      route.components && route.components.length > 0
        ? addDynamicPages(route, pageName)
        : addStaticPages(route, pageName);
    }
    // Add No-Match route (404)
    routes.push(<Route path="*" component={Common.NoRoute} key="no-route" />);
    return routes;
  }

    function addStaticPages(route, page) {
        let pageComponent = Home;
        let pageChild = null
        switch (page) {
            default:
            case "home":
                pageComponent = Home;
                break;
            case "cms-login":
                pageComponent = CmsLogin;
                break;
            case "cms-logout":
                pageComponent = CmsLogout;
                break;
            case "impact":
                pageComponent = ImpactHub;
                break;
            case "partners":
                pageComponent = PartnersPage;
                pageChild = <PartnersPage cms={cms}/>
                break;
            case "dollarMatching":
                pageComponent = DollarMatching;
                pageChild = <DollarMatching cms={cms}/>
                break;
              
        }
        routes.push(
            <Route
                exact
                path={route.url}
                component={pageComponent}
                key={page}
            >
             {pageChild !== null ? pageChild : null} 
            </Route>
        );
    }

    function getIndexRoutePath(url) {
        const currentLanguage = config.getLanguage();
        let indexRoutePath = "/";
        // Need to check if the page is a language page
        if (url.indexOf(`/${currentLanguage}/`) === 0 ) {
            indexRoutePath = `/${currentLanguage}/`;
        }
        return indexRoutePath;
    }

    function addDynamicPages(route, page) {
        const indexRoutePath = getIndexRoutePath(route.url);
        
        if (route.url && route.url !== indexRoutePath && route.components && route.url ) {
         
          routes.push(
                <Route
                exact
                path={route.url}
                key={page}
                >
                <DynamicPage cms={cms} page={page} componentList={route.components} />
                </Route>
            );
        }
        switch (page) {
            default:
            case "stories":
            case "content":
                addRoutes(route.components, page === "stories" ? "story" : page);
                break;
        }
    }

    function addRoutes(pages, pageName) {
      const currentLanguage = config.getLanguage();
      // The ^ and $ characters at the beginning and end of the regular expression pattern
      // respectively ensure that the entire page.url must match the pattern exactly
      const regex = new RegExp(`^(/${currentLanguage}/index|/index)$`, "g");
      pages.map((page) => {
        if(page && page.url){
          const pageHomeFound = regex.test(page.url);
          // Temporary homepage
          if (pageHomeFound) {
            const indexRoutePath = getIndexRoutePath(page.url);
            routes.push(
              // For Story, set randomKey to re-render this component to see changes on Preview
              <Route exact path={indexRoutePath} key={"homepage"}>
                <DynamicPage
                  cms={cms}
                  content={page}
                  page={pageName}
                  componentList={page.components}
                />
              </Route>
            );
          }
          routes.push(
            <Route
              exact
              path={page.url}
              key={pageName === "story" ? randomKey : page.id}
            >
              <DynamicPage
                cms={cms}
                page={pageName}
                content={page}
                componentList={page.components}
              />
            </Route>
          );
          return true;
        }
        return false;
      });
    }
};

export default MoRouter;
